/*
 * @Copyright 2025-2026. Institute for Future Intelligence, Inc.
 *
 * Convert between three molecule representations:
 *   - LLM-produced JSON   (preferred — strictly structured, schema-enforced)
 *   - LLM-produced SDF    (legacy / fallback — text-formatted, error-prone)
 *   - SDF text suitable for the strict SDF parser used by the renderer.
 *
 * Strategy: a single typed `Molecule` intermediate. Both JSON and SDF inputs
 * parse into the same structure; one strict V2000 emitter converts that
 * structure into a renderer-ready SDF. The emitter never produces malformed
 * output because every column is constructed from typed values.
 */

export interface MoleculeAtom {
  x: number;
  y: number;
  z: number;
  element: string;
  charge?: number; // partial atomic charge (e.g. Gasteiger), elementary-charge units
  // V2000 integer formal charge (−4..+4). Stored separately from `charge`
  // so the partial-charge round-trip via <PartialCharges> doesn't trample
  // it, and so emitSdf can put it in the canonical `M  CHG` property
  // line where downstream parsers expect to find it.
  formalCharge?: number;
  hybridization?: string; // sp | sp2 | sp3 | sp3d | sp3d2 | none
}

export interface MoleculeBond {
  a: number; // 0-based atom index (internal)
  b: number;
  order: number; // 1=single 2=double 3=triple 4=aromatic
}

export interface Molecule {
  title: string;
  atoms: MoleculeAtom[];
  bonds: MoleculeBond[];
  // Order-preserving property list. Some readers care about ordering
  // (e.g. SMILES typically appears before InChI in PubChem-style SDFs).
  properties: Map<string, string>;
}

// Periodic-table subset reasonable for organic / drug-like chemistry.
// Anything past Bi is rare enough that we'd rather reject it than risk
// confusing a coincidentally-capitalized token for an element symbol.
// prettier-ignore
const ELEMENT_SET = new Set<string>([
  'H', 'He', 'Li', 'Be', 'B', 'C', 'N', 'O', 'F', 'Ne',
  'Na', 'Mg', 'Al', 'Si', 'P', 'S', 'Cl', 'Ar', 'K', 'Ca',
  'Sc', 'Ti', 'V', 'Cr', 'Mn', 'Fe', 'Co', 'Ni', 'Cu', 'Zn',
  'Ga', 'Ge', 'As', 'Se', 'Br', 'Kr', 'Rb', 'Sr', 'Y', 'Zr',
  'Nb', 'Mo', 'Tc', 'Ru', 'Rh', 'Pd', 'Ag', 'Cd', 'In', 'Sn',
  'Sb', 'Te', 'I', 'Xe', 'Cs', 'Ba', 'La', 'Ce', 'Hf', 'Ta',
  'W', 'Re', 'Os', 'Ir', 'Pt', 'Au', 'Hg', 'Tl', 'Pb', 'Bi',
]);

// Canonical names for the property keys readSdfProps recognizes by literal lookup.
// Lowercase variants → canonical capitalization. Anything not listed falls back
// to whitespace-stripped name (handles e.g. "Polar Surface Area" implicitly).
const PROPERTY_NAME_MAP: Record<string, string> = {
  formula: 'Formula',
  'molecular formula': 'Formula',
  smiles: 'SMILES',
  inchi: 'InChI',
  logp: 'logP',
  complexity: 'Complexity',
  polarsurfacearea: 'PolarSurfaceArea',
  'polar surface area': 'PolarSurfaceArea',
  hydrogenbonddonors: 'HydrogenBondDonors',
  'hydrogen bond donors': 'HydrogenBondDonors',
  hydrogenbondacceptors: 'HydrogenBondAcceptors',
  'hydrogen bond acceptors': 'HydrogenBondAcceptors',
  rotatablebonds: 'RotatableBonds',
  'rotatable bonds': 'RotatableBonds',
  density: 'Density',
  meltingpoint: 'MeltingPoint',
  'melting point': 'MeltingPoint',
  boilingpoint: 'BoilingPoint',
  'boiling point': 'BoilingPoint',
  explanation: 'Explanation',
  partialcharges: 'PartialCharges',
  'partial charges': 'PartialCharges',
};

// Canonical key under which Gasteiger-style fractional charges are stashed
// in the SDF property block. V2000 has no native column for partial charges
// (its atom-block "charge" field encodes integer formal charges only), so we
// round-trip them through this data tag — one number per atom, in order.
const PARTIAL_CHARGES_KEY = 'PartialCharges';

const ATOM_RX = /^\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s+([A-Z][a-z]?)\b/;
const BOND_RX = /^\s*(\d+)\s+(\d+)(?:\s+(\d+(?:\.\d+)?|ar|aromatic))?(?:\s+\d+)*\s*$/i;
const PROPERTY_TAG_RX = /^>\s*(?:DT?\d+\s+)?<\s*([^>]+?)\s*>/;
const VERSION_RX = /V(?:2000|3000)\b/;
const END_BLOCK_RX = /^M\s+END\s*$/;
// V2000 formal-charge property line: `M  CHG  nn  aaa  vvv  aaa  vvv ...`
// where nn is the entry count and each (aaa, vvv) is a 1-based atom index
// and signed charge. We use this prefix to detect the line; payload tokens
// are extracted by whitespace-splitting the remainder.
const M_CHG_PREFIX_RX = /^M\s+CHG\b/;

const stripMarkdownAndProse = (raw: string): string => {
  let text = raw;
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
  text = text.trim();
  text = text.replace(/^[ \t]*```[a-zA-Z]*[ \t]*\r?\n/, '');
  text = text.replace(/\r?\n[ \t]*```[ \t]*$/, '');
  return text;
};

// Extract the first balanced JSON object from arbitrary text. Walks the
// string tracking quote state and brace depth so a `}` that appears
// inside a JSON string literal (e.g. a SMILES that happens to contain
// a brace, or an escaped sequence) doesn't prematurely close the outer
// object. Used as a fallback when the model wraps its JSON in prose
// like "Here is the molecule: { ... } Hope this helps!" — strict
// JSON.parse rejects that, but the embedded object is still recoverable.
// Returns null when no balanced `{ ... }` can be found.
const extractFirstJsonObject = (text: string): string | null => {
  const start = text.indexOf('{');
  if (start < 0) return null;
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (inString) {
      if (ch === '\\') escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === '{') depth++;
    else if (ch === '}') {
      depth--;
      if (depth === 0) return text.slice(start, i + 1);
    }
  }
  return null;
};

// Strict JSON.parse first; if it rejects, try to recover by extracting
// the first balanced { ... } substring. Throws SyntaxError when neither
// path produces a parseable object.
const parseJsonLoose = (input: string): unknown => {
  const cleaned = stripMarkdownAndProse(input);
  try {
    return JSON.parse(cleaned);
  } catch {
    const extracted = extractFirstJsonObject(cleaned);
    if (extracted === null) throw new SyntaxError('No JSON object found in response');
    return JSON.parse(extracted);
  }
};

const canonicalPropertyName = (name: string): string => {
  const key = name.toLowerCase().trim();
  if (PROPERTY_NAME_MAP[key]) return PROPERTY_NAME_MAP[key];
  const collapsed = key.replace(/\s+/g, '');
  if (PROPERTY_NAME_MAP[collapsed]) return PROPERTY_NAME_MAP[collapsed];
  return name.replace(/\s+/g, '');
};

const parseBondOrder = (raw: string | number | undefined): number => {
  if (raw === undefined) return 1;
  const lower = String(raw).toLowerCase().trim();
  if (lower === 'ar' || lower === 'aromatic') return 4;
  const n = parseFloat(lower);
  if (Number.isNaN(n)) return 1;
  if (n === 1.5) return 4;
  const rounded = Math.round(n);
  return rounded >= 1 && rounded <= 4 ? rounded : 1;
};

const cleanBonds = (bonds: MoleculeBond[], atomCount: number): MoleculeBond[] => {
  const seen = new Set<string>();
  const out: MoleculeBond[] = [];
  for (const bond of bonds) {
    if (bond.a < 0 || bond.b < 0 || bond.a >= atomCount || bond.b >= atomCount) continue;
    if (bond.a === bond.b) continue;
    const key = bond.a < bond.b ? `${bond.a}-${bond.b}` : `${bond.b}-${bond.a}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(bond);
  }
  return out;
};

// Liberal SDF parser: extract a Molecule from arbitrary LLM SDF output.
// Returns null if no atoms can be identified.
export const parseLooseSdf = (raw: string): Molecule | null => {
  const text = stripMarkdownAndProse(raw);
  const lines = text.split(/\r?\n/);

  let titleIdx = 0;
  while (titleIdx < lines.length && !lines[titleIdx].trim()) titleIdx++;
  const titleCandidate = lines[titleIdx]?.trim() ?? '';
  const isStructural =
    PROPERTY_TAG_RX.test(titleCandidate) ||
    ATOM_RX.test(lines[titleIdx] ?? '') ||
    END_BLOCK_RX.test(titleCandidate) ||
    titleCandidate === '$$$$' ||
    VERSION_RX.test(titleCandidate);
  const title = isStructural ? 'Generated' : titleCandidate || 'Generated';
  const startIdx = isStructural ? titleIdx : titleIdx + 1;

  const atoms: MoleculeAtom[] = [];
  const bonds: MoleculeBond[] = [];
  const properties = new Map<string, string>();

  let inAtomBlock = false;
  let pendingProp: { name: string; valueLines: string[] } | null = null;

  const flushProp = () => {
    if (pendingProp) {
      const value = pendingProp.valueLines.join('\n').trim();
      if (value) properties.set(pendingProp.name, value);
      pendingProp = null;
    }
  };

  for (let i = startIdx; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === '$$$$') {
      flushProp();
      break;
    }
    if (END_BLOCK_RX.test(trimmed)) {
      flushProp();
      inAtomBlock = false;
      continue;
    }
    // V2000 `M  CHG` lines come AFTER the bond block and BEFORE `M  END`.
    // Parse each (atom-index, charge) pair and write to the corresponding
    // atom's formalCharge. The leading count token is ignored — we just
    // walk the remaining tokens in pairs, which tolerates the rare case
    // of a writer emitting the count inconsistently with the entry list.
    if (M_CHG_PREFIX_RX.test(trimmed)) {
      const rest = trimmed.replace(M_CHG_PREFIX_RX, '').trim();
      const tokens = rest.split(/\s+/).filter(Boolean);
      // First token is the entry count; ignore it and pair up the rest.
      for (let k = 1; k + 1 < tokens.length; k += 2) {
        const a = parseInt(tokens[k], 10);
        const v = parseInt(tokens[k + 1], 10);
        if (Number.isFinite(a) && Number.isFinite(v) && a >= 1 && a <= atoms.length) {
          atoms[a - 1].formalCharge = v;
        }
      }
      continue;
    }
    if (!trimmed) {
      if (pendingProp) pendingProp.valueLines.push('');
      continue;
    }

    const propMatch = trimmed.match(PROPERTY_TAG_RX);
    if (propMatch) {
      flushProp();
      pendingProp = { name: canonicalPropertyName(propMatch[1]), valueLines: [] };
      continue;
    }

    if (pendingProp) {
      if (/^<\/[^>]+>\s*$/.test(trimmed)) continue;
      pendingProp.valueLines.push(trimmed);
      continue;
    }

    const atomMatch = line.match(ATOM_RX);
    if (atomMatch && ELEMENT_SET.has(atomMatch[4])) {
      atoms.push({
        x: parseFloat(atomMatch[1]),
        y: parseFloat(atomMatch[2]),
        z: parseFloat(atomMatch[3]),
        element: atomMatch[4],
      });
      inAtomBlock = true;
      continue;
    }

    if (inAtomBlock) {
      // V2000 bond lines are fixed-width: cols 1-3 atom1, 4-6 atom2, 7-9 type.
      // Try the fixed-width slice first; fall back to whitespace-tolerant
      // regex only when that doesn't parse. The whitespace-split regex
      // alone (BOND_RX) silently mangles 3-digit × 3-digit bonds like
      // "100101  1" (no space between indices) into a single token
      // "100101" → out-of-range atom → bond dropped. Same hazard the
      // correctSdf parser fixed in Util.ts.
      let fwA = NaN;
      let fwB = NaN;
      let fwOrder = '';
      if (line.length >= 9) {
        const f1 = line.substr(0, 3).trim();
        const f2 = line.substr(3, 3).trim();
        const f3 = line.substr(6, 3).trim();
        if (/^\d+$/.test(f1) && /^\d+$/.test(f2)) {
          fwA = parseInt(f1, 10);
          fwB = parseInt(f2, 10);
          fwOrder = /^\d+$/.test(f3) ? f3 : '';
        }
      }
      if (Number.isFinite(fwA) && Number.isFinite(fwB) && fwA >= 1 && fwB >= 1) {
        bonds.push({
          a: fwA - 1,
          b: fwB - 1,
          order: parseBondOrder(fwOrder),
        });
        continue;
      }
      const bondMatch = line.match(BOND_RX);
      if (bondMatch) {
        bonds.push({
          a: parseInt(bondMatch[1], 10) - 1,
          b: parseInt(bondMatch[2], 10) - 1,
          order: parseBondOrder(bondMatch[3]),
        });
        continue;
      }
    }
  }
  flushProp();

  if (atoms.length === 0) return null;

  // If a PartialCharges data tag was attached, fold its values into the
  // per-atom charge fields and drop the tag so it doesn't get re-emitted.
  // We tolerate both whitespace- and comma-separated values; one number per
  // atom is required (silently ignored otherwise — better than corrupting).
  const pcRaw = properties.get(PARTIAL_CHARGES_KEY);
  if (pcRaw) {
    const tokens = pcRaw.split(/[\s,]+/).filter(Boolean);
    if (tokens.length === atoms.length) {
      for (let i = 0; i < atoms.length; i++) {
        const v = parseFloat(tokens[i]);
        if (Number.isFinite(v)) atoms[i].charge = v;
      }
    }
    properties.delete(PARTIAL_CHARGES_KEY);
  }

  return { title, atoms, bonds: cleanBonds(bonds, atoms.length), properties };
};

// Strict V2000 SDF emitter. Every column is positioned by construction, so
// the output is guaranteed to satisfy a strict V2000 parser regardless of
// what the input looked like.
export const emitSdf = (m: Molecule): string => {
  const lines: string[] = [];

  // Header: title, program, comment.
  lines.push(m.title.slice(0, 80) || 'Generated');
  lines.push('  AIMS-Norm 3D');
  lines.push('');

  // Counts line.
  const a = String(m.atoms.length).padStart(3);
  const b = String(m.bonds.length).padStart(3);
  lines.push(`${a}${b}  0  0  0  0  0  0  0  0999 V2000`);

  for (const atom of m.atoms) {
    const x = atom.x.toFixed(4).padStart(10);
    const y = atom.y.toFixed(4).padStart(10);
    const z = atom.z.toFixed(4).padStart(10);
    const sym = atom.element.padEnd(3);
    lines.push(`${x}${y}${z} ${sym} 0  0  0  0  0  0  0  0  0  0  0  0`);
  }

  for (const bond of m.bonds) {
    const ai = String(bond.a + 1).padStart(3);
    const bi = String(bond.b + 1).padStart(3);
    const order = String(bond.order).padStart(3);
    lines.push(`${ai}${bi}${order}  0  0  0  0`);
  }

  // V2000 formal-charge property lines. Up to 8 (atom, charge) entries per
  // line, formatted to match OCL / Actelion's MolfileCreator output so
  // downstream tooling parses them identically. Atoms with formalCharge 0
  // (the default) are not emitted.
  const charged: { atomIndex: number; charge: number }[] = [];
  m.atoms.forEach((atom, i) => {
    const c = atom.formalCharge;
    if (Number.isFinite(c as number) && c !== 0) charged.push({ atomIndex: i, charge: c as number });
  });
  for (let i = 0; i < charged.length; i += 8) {
    const slice = charged.slice(i, i + 8);
    const count = String(slice.length).padStart(3);
    let line = `M  CHG${count}`;
    for (const { atomIndex, charge } of slice) {
      const ai = String(atomIndex + 1).padStart(4);
      // Sign + magnitude in the last 4 columns: " -2", "  1", etc.
      const chg = String(charge).padStart(4);
      line += `${ai}${chg}`;
    }
    lines.push(line);
  }

  lines.push('M  END');

  // Round-trip Gasteiger-style partial charges as a PartialCharges data tag.
  // One value per atom (in atom-block order), 4 decimal places. Any
  // PartialCharges entry already in the properties map is skipped below so
  // it doesn't appear twice.
  const hasCharges = m.atoms.some((a) => Number.isFinite(a.charge as number));
  if (hasCharges) {
    lines.push(`> <${PARTIAL_CHARGES_KEY}>`);
    for (const a of m.atoms) {
      const v = Number.isFinite(a.charge as number) ? (a.charge as number) : 0;
      lines.push(v.toFixed(4));
    }
    lines.push('');
  }

  for (const [name, value] of m.properties) {
    if (name === PARTIAL_CHARGES_KEY) continue;
    lines.push(`> <${name}>`);
    lines.push(value);
    lines.push('');
  }

  lines.push('$$$$');
  return lines.join('\n');
};

// Parse a JSON object (loosely typed; could be from JSON.parse of arbitrary
// text) into a Molecule. Returns null if the shape is unrecognizable.
const parseStructuredJson = (input: unknown): Molecule | null => {
  if (!input || typeof input !== 'object') return null;
  const root = input as Record<string, unknown>;
  const atomsRaw = root.atoms;
  if (!Array.isArray(atomsRaw) || atomsRaw.length === 0) return null;

  const atoms: MoleculeAtom[] = [];
  for (const a of atomsRaw) {
    if (!a || typeof a !== 'object') continue;
    const obj = a as Record<string, unknown>;
    const x = typeof obj.x === 'number' ? obj.x : parseFloat(String(obj.x));
    const y = typeof obj.y === 'number' ? obj.y : parseFloat(String(obj.y));
    const z = typeof obj.z === 'number' ? obj.z : parseFloat(String(obj.z));
    const element = typeof obj.element === 'string' ? obj.element.trim() : '';
    if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z) && ELEMENT_SET.has(element)) {
      const atom: MoleculeAtom = { x, y, z, element };
      const charge = typeof obj.charge === 'number' ? obj.charge : parseFloat(String(obj.charge));
      if (Number.isFinite(charge)) atom.charge = charge;
      if (typeof obj.hybridization === 'string' && obj.hybridization.trim()) {
        atom.hybridization = obj.hybridization.trim();
      }
      atoms.push(atom);
    }
  }
  if (atoms.length === 0) return null;

  const bonds: MoleculeBond[] = [];
  const bondsRaw = Array.isArray(root.bonds) ? root.bonds : [];
  for (const b of bondsRaw) {
    if (!b || typeof b !== 'object') continue;
    const obj = b as Record<string, unknown>;
    // LLMs vary between 1-based (SDF) and 0-based (JS). Accept both: if any
    // index is 0, assume 0-based for the whole bond list (we infer from min).
    const ra = Number(obj.a);
    const rb = Number(obj.b);
    if (!Number.isFinite(ra) || !Number.isFinite(rb)) continue;
    bonds.push({ a: ra, b: rb, order: parseBondOrder(obj.order as number | string | undefined) });
  }
  // Detect indexing convention. If any index is 0 the model used 0-based;
  // otherwise assume 1-based (the schema specifies 1-based).
  const minIdx = bonds.reduce((m, x) => Math.min(m, x.a, x.b), Number.POSITIVE_INFINITY);
  const oneBased = minIdx >= 1;
  const adjustedBonds = bonds.map((bond) => ({
    a: oneBased ? bond.a - 1 : bond.a,
    b: oneBased ? bond.b - 1 : bond.b,
    order: bond.order,
  }));

  const properties = new Map<string, string>();
  const propsRaw = root.properties;
  if (propsRaw && typeof propsRaw === 'object') {
    for (const [key, value] of Object.entries(propsRaw)) {
      if (value === null || value === undefined) continue;
      properties.set(canonicalPropertyName(key), String(value));
    }
  }

  const title = typeof root.title === 'string' && root.title.trim() ? root.title.trim() : 'Generated';

  return { title, atoms, bonds: cleanBonds(adjustedBonds, atoms.length), properties };
};

/**
 * Parse structured JSON (string or object) into the typed Molecule.
 * Returns null when the shape is unrecognizable. Useful when callers need
 * access to per-atom fields (charge, hybridization) that the SDF emitter drops.
 */
export const parseMoleculeJson = (input: string | object): Molecule | null => {
  let parsed: unknown;
  if (typeof input === 'string') {
    try {
      parsed = parseJsonLoose(input);
    } catch {
      return null;
    }
  } else {
    parsed = input;
  }
  return parseStructuredJson(parsed);
};

/**
 * Convert structured JSON (string or object) into renderer-ready SDF.
 * Throws if the input is not parseable as the molecule schema. Tolerates
 * conversational prose around the JSON object — the loose parser falls
 * back to extracting the first balanced { ... } when strict JSON.parse
 * rejects the input.
 */
export const jsonToSdf = (input: string | object): string => {
  const parsed = typeof input === 'string' ? parseJsonLoose(input) : input;
  const mol = parseStructuredJson(parsed);
  if (!mol) throw new Error('Invalid molecule JSON: no recognizable atoms');
  return emitSdf(mol);
};

/**
 * Multi-monomer parser used by the Polymer Studio "generate several
 * monomers in one prompt" path. Tries several shapes:
 *
 *   1. JSON array of molecule objects → one SDF per element.
 *   2. JSON object with `monomers: [ … ]` (or `molecules: [ … ]`) field
 *      → one SDF per element of the inner array.
 *   3. SDF multi-record text (records separated by lines containing
 *      `$$$$`) → one SDF per record.
 *
 * Each shape is tried independently; the parser returns the first one
 * that yields ≥ 2 valid SDFs. Returns null when no multi-monomer shape
 * is detected — callers should then fall back to the single-monomer
 * `jsonToSdf` / `tryNormalizeSdf` path. Items inside the array that
 * fail to parse are silently dropped; a partial parse (e.g. 3 of 4
 * monomers usable) still returns the 3 valid ones.
 */
export const parseManyMolecules = (raw: string): string[] | null => {
  // 1. JSON array or {monomers:[…]} / {molecules:[…]} object.
  const parsed = parseJsonLoose(raw);
  let array: unknown[] | null = null;
  if (Array.isArray(parsed)) {
    array = parsed;
  } else if (parsed && typeof parsed === 'object') {
    const obj = parsed as Record<string, unknown>;
    const candidates = [obj.monomers, obj.molecules, obj.items, obj.results];
    for (const c of candidates) {
      if (Array.isArray(c)) {
        array = c;
        break;
      }
    }
  }
  if (array && array.length > 0) {
    const sdfs: string[] = [];
    for (const item of array) {
      if (!item || typeof item !== 'object') continue;
      try {
        sdfs.push(jsonToSdf(item as object));
      } catch {
        // Skip malformed entries — partial parse is still useful.
      }
    }
    if (sdfs.length >= 2) return sdfs;
    if (sdfs.length === 1) return sdfs;
  }
  // 2. Multi-record SDF (RFC: records terminated by a line containing
  // only `$$$$`). Split, trim, filter blanks; each record needs to
  // parse via tryNormalizeSdf to be counted.
  const records = raw
    .split(/\r?\n\$\$\$\$\r?\n?/)
    .map((r) => r.trim())
    .filter((r) => r.length > 0);
  if (records.length >= 2) {
    const sdfs: string[] = [];
    for (const r of records) {
      const norm = tryNormalizeSdf(r);
      if (norm) sdfs.push(norm);
    }
    if (sdfs.length >= 2) return sdfs;
  }
  return null;
};

/**
 * Convert SDF text (any quality) into the JSON intermediate. Used to
 * re-feed past assistant turns in chat history when switching to JSON mode.
 * Returns null if no atoms could be parsed.
 */
export const sdfToJsonString = (sdf: string): string | null => {
  const mol = parseLooseSdf(sdf);
  if (!mol) return null;
  return JSON.stringify({
    title: mol.title,
    atoms: mol.atoms,
    bonds: mol.bonds.map((b) => ({ a: b.a + 1, b: b.b + 1, order: b.order })),
    properties: Object.fromEntries(mol.properties),
  });
};

/**
 * Bake an array of partial charges into the SDF as a `<PartialCharges>` data
 * tag. The charge array length must match the SDF's atom count; mismatches
 * return null. Returns the new SDF text on success, or null if the SDF
 * cannot be parsed or the count check fails.
 */
export const writeSdfPartialCharges = (sdf: string, charges: number[]): string | null => {
  const mol = parseLooseSdf(sdf);
  if (!mol) return null;
  if (charges.length !== mol.atoms.length) return null;
  for (let i = 0; i < mol.atoms.length; i++) {
    const v = charges[i];
    if (Number.isFinite(v)) mol.atoms[i].charge = v;
  }
  return emitSdf(mol);
};

/**
 * Read partial charges out of an SDF (one value per atom). Returns null if no
 * PartialCharges tag is present or its length doesn't match the atom count.
 */
export const readSdfPartialCharges = (sdf: string): number[] | null => {
  const mol = parseLooseSdf(sdf);
  if (!mol) return null;
  const charges = mol.atoms.map((a) => (Number.isFinite(a.charge as number) ? (a.charge as number) : NaN));
  return charges.some((c) => Number.isFinite(c)) ? charges : null;
};

/**
 * Lenient SDF normalizer. Used as fallback when the model returns text-form
 * SDF instead of JSON, or when JSON parsing fails. Returns the input unchanged
 * if no atoms could be extracted (downstream parser surfaces the error).
 *
 * Most callers should prefer `tryNormalizeSdf` — it signals the failure
 * case explicitly instead of returning unparseable input.
 */
export const normalizeSdf = (raw: string): string => {
  if (!raw) return raw;
  const mol = parseLooseSdf(raw);
  if (!mol) return raw;
  return emitSdf(mol);
};

/**
 * Variant of `normalizeSdf` that returns null when the input can't be
 * coerced to V2000 — i.e. `parseLooseSdf` couldn't find any atoms.
 * Callers can branch on this to surface a real error to the user
 * instead of committing the raw model response as molecule data.
 */
export const tryNormalizeSdf = (raw: string): string | null => {
  if (!raw) return null;
  const mol = parseLooseSdf(raw);
  if (!mol) return null;
  return emitSdf(mol);
};
